import React, { useCallback, Fragment, useState, useEffect } from 'react'
import { IconAddUser, IconEwiInfo } from 'components/Zui/Icon'
import { useAuth, useConfirm, useToggle, useIntl, useToast } from 'hooks'
import {
    useInviteUserMutation,
    useUpdateUserMutation,
    useGetUsersQuery,
    useDeleteUserMutation,
} from 'services/user'
import { arrayPartition } from 'utils/helpers'
import config from 'app/config'
import { TableLoader } from 'components/Loaders'
import UsersListTable from 'components/UsersListTable'
import ContentTitle from 'components/ContentTitle'
import Invite from './Invite'
import EditUser from './EditUser'
import UserDetails from './UserDetails'
import { Button, InlineMessage } from 'components/Zui'
import './Users.css'

const UserManagement = () => {
    var users = []
    const { t } = useIntl()
    const { toast } = useToast()
    const { getConfirmation } = useConfirm()
    const { authUser, isAuthorized, fetchUserData } = useAuth()
    const [showTooltip, setShowTooltip] = useState(false)
    const [userDetailsEmail, setUserDetailsEmail] = useState()
    const { isOpen, onOpen, onClose, onToggle } = useToggle()
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useToggle()
    const {
        isOpen: isUserDetailsOpen,
        onOpen: onUserDetailsOpen,
        onClose: onUserDetailsClose,
    } = useToggle()

    const [inviteUser, { status: inviteStatus, isLoading: isInviting, error: inviteError }] =
        useInviteUserMutation()
    const [updateUser, { status: updateStatus }] = useUpdateUserMutation()
    const [deleteUser] = useDeleteUserMutation()
    const { data, isLoading, error } = useGetUsersQuery(authUser.agreementId, {
        skip: !isAuthorized,
    })

    useEffect(() => {
        if (inviteStatus === 'rejected') {
            toast.error(inviteError.name, { description: inviteError.message })
        }
    }, [inviteStatus])

    useEffect(() => {
        if (updateStatus === 'fulfilled') {
            fetchUserData()
        }
    }, [updateStatus])

    if (!isLoading && data) {
        const [pass, fail] = arrayPartition(data, e => e.id === authUser.id)
        users = [...pass, ...fail]
    }

    const canInvite = useCallback(() => {
        if (data?.length < config.SUBSCRIPTION.MAX_USER_INVITATIONS && !error && !isInviting) {
            return true
        }
        return false
    }, [data, error, isInviting])

    const handleInviteUser = val => {
        const inviteData = {
            email: val.email,
        }
        onToggle()
        inviteUser(inviteData)
    }

    const handleUpdateUser = val => {
        const values = {}
        for (const key in val) {
            values[key] = typeof val[key] === 'string' ? val[key] : val[key].join()
        }
        const { email, status, ...payload } = values

        onEditClose()
        updateUser(payload)
    }

    const handleDeleteUser = async userId => {
        const confirmed = await getConfirmation({
            title: t('users.confirm.remove.title'),
            message: t('users.confirm.remove.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (confirmed) {
            const user = users.find(user => user.id === userId)
            const { agreementId, id } = user
            deleteUser({ agreementId, id, isWithdraw: false })
        }
    }

    const handleWithdrawInvitaion = async userId => {
        const confirmed = await getConfirmation({
            title: t('users.confirm.withdraw.title'),
            message: t('users.confirm.withdraw.message', { appName: config.APP_NAME }),
            buttons: [t('cancel'), t('withdraw')],
        })

        if (confirmed) {
            const user = users.find(user => user.id === userId)
            const { agreementId, id } = user
            deleteUser({ agreementId, id, isWithdraw: true })
        }
    }

    const handleOpenUserDetails = async emailId => {
        await setUserDetailsEmail(emailId)
        onUserDetailsOpen()
    }

    return (
        <Fragment>
            <div className="user-management-wrapper">
                <div className="user-management-header">
                    <ContentTitle>
                        <span>{t('user.management.title')}</span>
                    </ContentTitle>
                    <div className="invite-section">
                        {data?.length >= config.SUBSCRIPTION.MAX_USER_INVITATIONS && (
                            <>
                                <div className="user-limit-messsage-xl">
                                    <InlineMessage style={{ width: '637px' }}>
                                        {t('max.user.limit')}
                                        <IconEwiInfo
                                            style={{ height: '14px', width: '14px' }}
                                            slot="icon"
                                            size="m"
                                        ></IconEwiInfo>
                                    </InlineMessage>
                                </div>
                                <div className="user-limit-messsage-sm">
                                    <IconEwiInfo
                                        style={{ height: '14px', width: '14px' }}
                                        slot="icon"
                                        size="m"
                                        onClick={() => setShowTooltip(!showTooltip)}
                                    ></IconEwiInfo>

                                    {showTooltip && (
                                        <div className="user-limit-popup-message">
                                            <zui-tooltip>
                                                <p className="user-limit-message">
                                                    <span className="user-limit-message-bold">
                                                        {t('max.user.reached')}
                                                    </span>
                                                    {t('user.delete.warning')}
                                                </p>
                                            </zui-tooltip>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        <Button
                            emphasis="primary-highlight"
                            onClick={onOpen}
                            disabled={!canInvite()}
                        >
                            <IconAddUser slot="icon" />
                            {t('invite.user')}
                        </Button>
                    </div>
                </div>

                {!isLoading ? (
                    <UsersListTable
                        data={users}
                        onDeleteUser={handleDeleteUser}
                        onWithdrawInvitation={handleWithdrawInvitaion}
                        onEditOpen={onEditOpen}
                        onUserDetailsOpen={handleOpenUserDetails}
                    />
                ) : (
                    <TableLoader />
                )}
            </div>
            {isOpen && canInvite() && (
                <Invite onSubmit={handleInviteUser} onClose={onClose} userCount={users.length} />
            )}
            {isEditOpen && (
                <EditUser data={users[0]} onSubmit={handleUpdateUser} onClose={onEditClose} />
            )}

            {isUserDetailsOpen && (
                <UserDetails email={userDetailsEmail} onClose={onUserDetailsClose} />
            )}
        </Fragment>
    )
}

export default UserManagement
